/**
 * DrawerValueEditor — scalar value editor hosted inside the Edit & Verify
 * drawer for the two rows whose value is a single API-backed field:
 *
 *   - credit     → credit_score_range (credit band select)
 *   - employment → employment_months  (work-history select)
 *
 * The passport redesign moved every row's edit affordance into the drawer but
 * left the drawer a sources-only shell, which silently dropped the ability to
 * set the credit band and work history (both still supported by PATCH
 * /api/profile). This component restores those editors, reusing the SAME
 * useProfileEdit mutation path and option sets as the inline rows — no new
 * edit path is introduced.
 */

import { useState } from "react";

import type { DashboardSnapshot } from "../../../lib/dashboard";
import {
  useProfileEdit,
  type UseProfileEditOptions,
} from "../../../lib/profile/use-profile-edit";
import { Select } from "../../ui/select";
import { CREDIT_BAND_ITEMS } from "./rows/credit-row.helpers";
import { workHistoryOptions } from "./rows/build-aggregate-rows";
import { buildProfilePatch } from "./DrawerValueEditor.helpers";

/** The rows whose value is a single PATCH /api/profile field. */
export type DrawerEditableRow = "credit" | "employment";

const ROW_CONFIG: Record<
  DrawerEditableRow,
  {
    items: Array<{ label: string; value: string }>;
    label: string;
    placeholder: string;
  }
> = {
  credit: {
    items: CREDIT_BAND_ITEMS,
    label: "Credit band",
    placeholder: "Select your band",
  },
  employment: {
    items: workHistoryOptions,
    label: "Work history",
    placeholder: "Select work history",
  },
};

function currentValueForRow(
  rowId: DrawerEditableRow,
  snapshot: DashboardSnapshot,
): string {
  if (rowId === "credit") return snapshot.creditBucket ?? "";
  return snapshot.employmentMonths != null
    ? String(snapshot.employmentMonths)
    : "";
}

export function DrawerValueEditor({
  rowId,
  snapshot,
  opts,
}: {
  rowId: DrawerEditableRow;
  snapshot: DashboardSnapshot;
  opts: UseProfileEditOptions;
}) {
  const editProfile = useProfileEdit(opts);

  const initial = currentValueForRow(rowId, snapshot);
  // Rebase the draft whenever the resolved snapshot value changes (after a
  // successful save), mirroring CreditRow's draft-vs-initial pattern.
  const [draftState, setDraftState] = useState({ base: initial, value: initial });
  const draft = draftState.base === initial ? draftState.value : initial;
  const setDraft = (value: string) => setDraftState({ base: initial, value });

  const config = ROW_CONFIG[rowId];
  const error = editProfile.error
    ? "Couldn't save — please try again."
    : undefined;

  const handleChange = (next: string) => {
    if (next === initial) {
      setDraft(next);
      return;
    }
    setDraft(next);
    const patch = buildProfilePatch(rowId, next);
    if (!patch) return;
    editProfile.mutate(patch).catch(() => {
      // Revert the optimistic draft on failure; the error message surfaces
      // via the Select's error prop.
      setDraft(initial);
    });
  };

  return (
    <div className="space-y-3" data-slot="drawer-value-editor">
      <Select
        disabled={editProfile.isPending}
        error={error}
        items={config.items}
        label={config.label}
        onValueChange={handleChange}
        placeholder={config.placeholder}
        value={draft}
      />
    </div>
  );
}
